import { useEffect, useMemo, useState } from 'react';
import { execApi } from '../../../services/api';
import { usePermissions } from '../../../hooks/usePermissions';
import { PatientCardLink } from '../../PatientCardLink';
import { JourneyFlow } from './JourneyFlow';

// Flexible patient-journey cohort: pick an anchor milestone + month(s) it happened
// → forward funnel (how many reached each later milestone) + drop-off reasons.

const ANCHORS: { key: string; label: string }[] = [
  { key: 'registration', label: 'מילוי שאלון' },
  { key: 'cm_intake', label: 'אינטייק מנהל-תיק' },
  { key: 'psy_intake', label: 'אינטייק פסיכיאטר' },
  // מעקב CM 1-4 were removed as anchors (Ohad, 2026-10-08). They carried a SECOND,
  // contradicting definition of the same name: the legacy panels count CM follow-ups
  // once per patient after the psychiatrist intake, while the journey counts them per
  // cycle, resetting at every psychiatrist visit. "did 4 CM follow-ups" is 944 patients
  // under one and 2,130 under the other, on the same screen under the same words.
  // "A case manager's patients" is already answerable: anchor on the CM intake and
  // filter by therapist — the same people, without the ambiguity.
  // The backend still accepts them (`_COL`, `_ANCHOR_MTYPES`); this is only the picker.
  { key: 'psy_fu1', label: 'מעקב פסיכ׳ 1' },
  { key: 'psy_fu2', label: 'מעקב פסיכ׳ 2' },
  { key: 'psy_fu3', label: 'מעקב פסיכ׳ 3' },
  { key: 'psy_fu4', label: 'מעקב פסיכ׳ 4' },
];

const HE_MONTHS = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
const CLINICS = ['Maccabi', 'Clalit', 'Maccabi Youth', 'ITC', 'Private', 'Donation'];
// Colour by outcome CATEGORY (matches the "current" chart): finished=gray, stuck=red, scheduled=green.
const CAT_COLOR: Record<string, string> = {
  finished: 'var(--ex-text-muted)',
  stuck: 'var(--ex-red-bar, #EF4444)',
  scheduled: 'var(--ex-green-bar)',
};

// Anchors the cyclic journey can start from — every one is a point on the psychiatrist spine.
const SEQ_ANCHORS = ['registration', 'cm_intake', 'psy_intake',
                     'psy_fu1', 'psy_fu2', 'psy_fu3', 'psy_fu4'];

const fmt = (n: number) => n.toLocaleString();
const CLINICUI = 'https://clinicui.taliaz-cm.com/patient';   // deep-link ?id=<internal_id>

export function CohortJourney() {
  const canOpenPatient = usePermissions().can('patient_card');
  const months = useMemo(() => {
    const out: { id: string; label: string }[] = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      out.push({ id: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, label: `${HE_MONTHS[d.getMonth()]} ${d.getFullYear()}` });
    }
    return out;
  }, []);

  const [anchor, setAnchor] = useState('registration');
  const [selMonths, setSelMonths] = useState<string[]>([months[0].id]);
  const [monthsOpen, setMonthsOpen] = useState(false);
  const [clinicFilter, setClinicFilter] = useState('');
  const [therapistFilter, setTherapistFilter] = useState('');
  const [therapists, setTherapists] = useState<{ name: string; type: string }[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [data, setData] = useState<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [seqData, setSeqData] = useState<any>(null);   // cyclic next-stages sequence
  // Money is off by default: the funnel's first job is to show where patients go, and
  // a shekel figure beside every bar competes with that. On, it answers the second
  // question — what a drop-off at this step costs (Ohad, 2026-10-07).
  const [showMoney, setShowMoney] = useState(false);
  const [openCyc, setOpenCyc] = useState<Set<string>>(new Set());   // expanded CM-cycle stages (funnel)
  const [openCur, setOpenCur] = useState<Set<string>>(new Set());   // expanded CM stages ("where are they now")
  const [loading, setLoading] = useState(false);
  // Click-to-list: the stuck patients behind a "where are they now" stage.
  const [stuckSel, setStuckSel] = useState<{ key: string; label: string } | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [stuckList, setStuckList] = useState<any[] | null>(null);
  const [stuckLoading, setStuckLoading] = useState(false);

  // Therapist dropdown options — fetched once from the staff roster, filtered to the
  // anchor's role (psy anchor → psychiatrists, cm anchor → case managers).
  useEffect(() => {
    execApi.getStaff(months[0].id)
      .then((r) => {
        const list = (r.data?.therapists ?? []) as { name: string; type: string }[];
        setTherapists(list.filter((t) => t.name).map((t) => ({ name: t.name, type: t.type })));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const anchorRole = anchor.startsWith('cm') ? 'cm' : anchor.startsWith('psy') ? 'psy' : '';
  const therapistOptions = useMemo(
    () => therapists.filter((t) => !anchorRole || t.type === anchorRole)
      .map((t) => t.name).sort((a, b) => a.localeCompare(b)),
    [therapists, anchorRole],
  );

  // Load on demand (Ohad 2026-07-08): fetch only when `applied` changes — initial mount + the
  // "הצג נתונים" button — NOT on every filter tweak.
  // Start EMPTY → nothing loads on arrival; the panel stays "closed" until the user picks
  // filters and clicks "הצג נתונים". (Ohad 2026-07-08)
  const [applied, setApplied] = useState<{ anchor: string; months: string[]; clinic: string; therapist: string }>(
    { anchor: '', months: [], clinic: '', therapist: '' });
  const showResults = applied.months.length > 0;
  const fkey = (a: string, m: string[], c: string, t: string) => `${a}|${[...m].sort().join(',')}|${c}|${t}`;
  const appliedKey = fkey(applied.anchor, applied.months, applied.clinic, applied.therapist);
  const dirty = appliedKey !== fkey(anchor, selMonths, clinicFilter, therapistFilter);
  const loadNow = () => setApplied({ anchor, months: selMonths, clinic: clinicFilter, therapist: therapistFilter });
  const openStuck = (key: string, label: string) => {
    setStuckSel({ key, label });
    setStuckList(null);
    setStuckLoading(true);
    const cl = applied.clinic ? [applied.clinic] : undefined;
    const th = applied.therapist ? [applied.therapist] : undefined;
    execApi.getCohortPatients(applied.anchor, applied.months, key, 'stuck', cl, th)
      .then((r) => setStuckList(r.data ?? []))
      .catch(() => setStuckList([]))
      .finally(() => setStuckLoading(false));
  };
  useEffect(() => {
    if (!applied.months.length) return;
    let alive = true;
    setLoading(true);
    const cl = applied.clinic ? [applied.clinic] : undefined;
    const th = applied.therapist ? [applied.therapist] : undefined;
    execApi.getCohortJourney(applied.anchor, applied.months, cl, th)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    // The journey's spine is the psychiatrist visits; a CM follow-up is not a point on it
    // (it happens inside every cycle), so there is no defined "where does cycle 1 start"
    // for those anchors. They used to blank the panel with no explanation.
    const seqOk = SEQ_ANCHORS.includes(applied.anchor);
    setOpenCyc(new Set()); setOpenCur(new Set()); setStuckSel(null); setStuckList(null);
    if (seqOk) {
      execApi.getCohortSequence(applied.anchor, applied.months, cl, th)
        .then((r) => { if (alive) setSeqData(r.data); })
        .catch(() => { if (alive) setSeqData(null); });
    } else if (alive) { setSeqData(null); }
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedKey]);

  const toggleMonth = (id: string) => {
    setSelMonths((prev) => prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]);
  };
  const toggleCyc = (k: string) =>
    setOpenCyc((p) => { const n = new Set(p); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const toggleCur = (k: string) =>
    setOpenCur((p) => { const n = new Set(p); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  // Cohort size: prefer the sequence's (ALL who did the anchor — incl. registrants who never had
  // a meeting) over the legacy funnel's (which silently dropped no-meeting patients). Ohad 2026-07-08.
  const cohort = (seqData?.cohort_total ?? data?.cohort_total) ?? 0;
  // "Where are they now" — prefer the CYCLIC exclusive stages (per-cycle CM, psy-fu 1..4) when
  // the sequence is available; fall back to the legacy flat funnel for unsupported anchors.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const current: any[] = (seqData?.current?.length ? seqData.current : data?.current) ?? [];
  // "Where are they now" shows ACTIVE patients only (has a future meeting OR stuck), NOT finished.
  const curActive = (s: { stuck?: number; scheduled?: number }) => (s.stuck ?? 0) + (s.scheduled ?? 0);
  const curMax = Math.max(1, ...current.map(curActive));
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const closure: any[] = (seqData?.closure?.length ? seqData.closure : data?.closure) ?? [];
  const closureTotal = closure.reduce((a: number, c: { count: number }) => a + c.count, 0);

  return (
    <div dir="rtl">
      <div className="pat-unified"><div className="pat-unified-section">
        <div className="pat-panel-head">
          <span className="pat-panel-title" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            מסע מטופל · קוהורט קדימה
            {loading && <span className="pat-spin" role="status" aria-label="טוען" />}
          </span>
          <span className="pat-panel-sub">בחר אבן-דרך + חודש → לחץ "הצג נתונים" → מה קרה לאותם מטופלים עד היום (משפך + איפה נמצאים + סיבות-נשירה).</span>
        </div>
      </div></div>

      <div className="pat-filter-bar">
        <label className="pat-filter">
          <span className="pat-filter-label">אבן-דרך (עוגן)</span>
          <select className="pat-cohort-period-select" value={anchor} onChange={(e) => { setAnchor(e.target.value); setTherapistFilter(''); }}>
            {ANCHORS.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
          </select>
        </label>
        <div className="pat-filter" style={{ position: 'relative' }}>
          <span className="pat-filter-label">חודש (מתי קרה העוגן)</span>
          <button type="button" className="pat-cohort-period-select" onClick={() => setMonthsOpen((o) => !o)} style={{ textAlign: 'right', minWidth: 180 }}>
            {selMonths.length === 0 ? 'בחר חודש' : selMonths.length === 1 ? months.find((m) => m.id === selMonths[0])?.label : `${selMonths.length} חודשים נבחרו`}
          </button>
          {monthsOpen && (
            <>
              <div onClick={() => setMonthsOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 20 }} />
              <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 21, marginTop: 4, background: 'var(--ex-bg-card)', border: '1px solid var(--ex-border)', borderRadius: 8, padding: 6, maxHeight: 260, overflowY: 'auto', minWidth: 180, boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}>
                {months.map((m) => (
                  <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', cursor: 'pointer', fontSize: 13, color: 'var(--ex-text-secondary)' }}>
                    <input type="checkbox" checked={selMonths.includes(m.id)} onChange={() => toggleMonth(m.id)} />
                    {m.label}
                  </label>
                ))}
              </div>
            </>
          )}
        </div>
        <label className="pat-filter">
          <span className="pat-filter-label">קופה</span>
          <select className="pat-cohort-period-select" value={clinicFilter} onChange={(e) => setClinicFilter(e.target.value)}>
            <option value="">כל הקופות</option>
            {CLINICS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className="pat-filter">
          <span className="pat-filter-label">מטפל (לפי העוגן)</span>
          <select className="pat-cohort-period-select" value={therapistFilter} onChange={(e) => setTherapistFilter(e.target.value)} disabled={!anchorRole} style={{ minWidth: 180 }}>
            <option value="">כל המטפלים</option>
            {therapistOptions.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <div className="pat-filter">
          <span className="pat-filter-label">&nbsp;</span>
          <button type="button" onClick={loadNow} disabled={loading} className="pat-cohort-period-select"
            style={{ cursor: loading ? 'default' : 'pointer', fontWeight: 700, minWidth: 120,
              background: dirty && !loading ? 'var(--ex-blue-bar)' : undefined,
              color: dirty && !loading ? '#fff' : undefined,
              borderColor: dirty && !loading ? 'var(--ex-blue-bar)' : undefined }}>
            {loading ? 'טוען…' : dirty ? 'הצג נתונים ●' : 'הצג נתונים'}
          </button>
        </div>
      </div>

      {!showResults ? (
        <div className="pat-unified"><div className="pat-unified-section" style={{ textAlign: 'center', color: 'var(--ex-text-muted)', padding: '22px 0', fontSize: 14 }}>
          בחר אבן-דרך + חודש ולחץ "הצג נתונים".
        </div></div>
      ) : (
        <div className="pat-cohort-headline">
          <span className="pat-cohort-headline-num">{fmt(cohort)}</span>
          <span className="pat-cohort-headline-label">מטופלים עשו {data?.anchor_label ?? ''} בחודשים הנבחרים</span>
        </div>
      )}

      {/* THE REAL JOURNEY — every stage, and inside every CM cycle every follow-up level.
          Replaced a flat per-stage version that could not answer the question it was built
          for: a cycle's four levels were one bucket, so "stopped after one follow-up" and
          "stopped after four" read identically (Ohad, 2026-10-07). */}
      {showResults && !SEQ_ANCHORS.includes(applied.anchor) && (
        <div className="pat-unified"><div className="pat-unified-section">
          <div className="pat-panel-head">
            <span className="pat-panel-title">מסע המטופל — שלב אחר שלב</span>
            <span className="pat-panel-sub">
              המסע נמדד לפי ביקורי הפסיכיאטר, ומעקב מנהל-תיק אינו נקודה על הציר הזה — הוא
              חוזר בתוך כל סבב. בחרו נקודת פתיחה אחרת כדי לראות אותו:
              {' '}{ANCHORS.filter((a) => SEQ_ANCHORS.includes(a.key)).map((a) => a.label).join(' · ')}.
            </span>
          </div>
        </div></div>
      )}

      {showResults && seqData?.sequence?.length > 0 && (
        <div className="pat-unified"><div className="pat-unified-section">
          <div className="pat-panel-head">
            <span className="pat-panel-title">מסע המטופל — שלב אחר שלב</span>
            <span className="pat-panel-sub">
              כל סבב נפתח למעקבי מנהל-התיק שבתוכו. בכל רמה: מי המשיך למעקב הבא, מי עבר
              לפסיכיאטר, ומי עצר — ומתחת למי שעצר, הסיבות. "תור עתידי מתוכנן" אינו עצירה.
            </span>
          </div>
          <JourneyFlow sequence={seqData.sequence} current={seqData.current ?? []}
                       anchorLabel={data?.anchor_label ?? ''} />
        </div></div>
      )}

      {/* NEXT STAGES — cyclic sequence after the anchor. CM follow-ups collapse into one
          stage per cycle; click to expand its 1/2/3/4 split. */}
      {showResults && seqData?.sequence?.length > 0 && (
        <div className="pat-unified"><div className="pat-unified-section">
          <div className="pat-panel-head">
            <span className="pat-panel-title">השלבים הבאים · מסע מחזורי</span>
            <button type="button" onClick={() => setShowMoney((v) => !v)}
              title="מה שווה כל שלב, ומה עולה נשירה בו — תעריף הקופה פחות התשלום למטפל. מכבי, לפני מע״מ"
              style={{ marginInlineStart: 'auto', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                       border: '1px solid var(--ex-border)', borderRadius: 7, padding: '4px 10px',
                       background: showMoney ? '#EF4444' : 'var(--ex-bg-soft)',
                       color: showMoney ? '#fff' : 'var(--ex-text-secondary)' }}>
              ₪ {showMoney ? 'מסתיר כסף' : 'מה זה עולה'}
            </button>
            <span className="pat-panel-sub">משפך: כל בר = מי שהגיע לשלב (הולך וקטן). המספר הלבן = כמה בשלב; <b style={{ color: 'var(--ex-green-bar)' }}>"דילגו"</b> (ירוק) = כמה קפצו מכאן ישר למעקב הפסיכיאטר הבא בלי להשלים את מחזור ה-CM (בשלב CM = עשו בדיוק כך מעקבים ואז קפצו; ↩ בשלב פסיכ׳ = 0 מעקבים). כחול = אבן-דרך, ירוק = מעקב מנהל-תיק (לחיץ → 2/3/4).</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 660 }}>
            {seqData.sequence.map((st: { key: string; label: string; kind: string; reached: number; pct: number; entered: number; avg_cm: number; skip: number; progressed: number; prog_pct: number; pct_of_prev: number; dropped: number; value_per_patient: number; revenue_per_patient: number; recognized_per_patient: number; cost_per_patient: number; value_cumulative: number; steps: { n: number; reached: number; pct: number; progressed: number; prog_pct: number }[] }, i: number) => {
              // Conversion comes from the server now. It used to be recomputed here,
              // which looked identical and was not: the server subtracts patients with
              // a future appointment before calling them a drop-off (Ohad 2026-10-07).
              const pctPrev = Math.round(st.pct_of_prev ?? 100);
              return st.kind === 'milestone' ? (
                <div key={st.key} style={{ display: 'grid', gridTemplateColumns: '150px 1fr 172px', gap: 10, alignItems: 'center', padding: '4px 0' }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--ex-text-primary)' }}>{st.label}</span>
                  <div style={{ height: 18, borderRadius: 4, background: 'var(--ex-bg-panel)', overflow: 'hidden' }}>
                    <div style={{ width: `${cohort ? (st.reached / cohort) * 100 : 0}%`, height: '100%', background: 'var(--ex-blue-bar)', borderRadius: 4 }} />
                  </div>
                  <span style={{ fontSize: 13, textAlign: 'left', color: 'var(--ex-text-secondary)' }}>
                    <b style={{ color: 'var(--ex-text-primary)' }}>{fmt(st.reached)}</b> · {pctPrev}%
                    {st.skip > 0 && <span style={{ color: 'var(--ex-green-bar)', fontWeight: 700 }} title="קפצו מכאן ישר למעקב הפסיכיאטר הבא (בדילוג על מעקב מנהל-תיק)"> · ↩{fmt(st.skip)}</span>}
                    {showMoney && (
                      <span style={{ color: st.value_per_patient < 0 ? '#EF4444' : 'var(--ex-text-secondary)', fontWeight: 700 }}
                        title={`הקופה משלמת ₪${st.revenue_per_patient} · הוכר לשלב ₪${st.recognized_per_patient} (שליש מהמנוי באינטייק מנהל התיק, שני שליש אצל הפסיכיאטר) · אנחנו משלמים ₪${st.cost_per_patient} · רווח ₪${st.value_per_patient} לשלב. מטופל שהגיע עד כאן שווה ₪${Math.round(st.value_cumulative).toLocaleString('he-IL')} רווח מצטבר`}>
                        {' '}· ₪{Math.round(st.value_cumulative).toLocaleString('he-IL')}
                      </span>
                    )}
                  </span>
                </div>
              ) : (
                <div key={st.key} style={{ paddingInlineStart: 16, borderInlineStart: '2px dashed rgba(148,163,184,0.3)' }}>
                  <button onClick={() => toggleCyc(st.key)}
                    style={{ display: 'grid', gridTemplateColumns: '134px 1fr 172px', gap: 10, alignItems: 'center', width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '3px 0', font: 'inherit' }}>
                    <span style={{ fontSize: 13, color: 'var(--ex-text-secondary)', display: 'flex', alignItems: 'center', gap: 5 }}>
                      <span style={{ transform: openCyc.has(st.key) ? 'rotate(90deg)' : 'none', display: 'inline-block', transition: 'transform .12s' }}>▸</span>{st.label}
                    </span>
                    <div style={{ height: 14, borderRadius: 3, background: 'var(--ex-bg-panel)', overflow: 'hidden' }}>
                      <div style={{ width: `${cohort ? (st.reached / cohort) * 100 : 0}%`, height: '100%', background: 'var(--ex-green-bar, #0D9488)', borderRadius: 3 }} />
                    </div>
                    <span style={{ fontSize: 12.5, textAlign: 'left', color: 'var(--ex-text-muted)' }}>
                      <b style={{ color: 'var(--ex-text-secondary)' }}>{fmt(st.reached)}</b> · {pctPrev}%
                      {st.progressed > 0 && <span style={{ color: 'var(--ex-green-bar)', fontWeight: 700 }} title="עשו בדיוק מעקב מנהל-תיק אחד במחזור ואז דילגו למעקב הפסיכיאטר (בלי להשלים את המחזור)"> · דילגו {fmt(st.progressed)}</span>}
                    </span>
                  </button>
                  {openCyc.has(st.key) && (
                    <div style={{ padding: '2px 0 6px 18px' }}>
                      {st.steps.map((sub, j: number) => {
                        const psub = j > 0 ? st.steps[j - 1].reached : st.reached;
                        const spct = psub ? Math.round((sub.reached / psub) * 100) : 0;
                        return (
                          <div key={sub.n} style={{ display: 'grid', gridTemplateColumns: '134px 1fr 150px', gap: 10, alignItems: 'center', padding: '2px 0' }}>
                            <span style={{ fontSize: 12.5, color: 'var(--ex-text-secondary)' }}>מעקב מנהל תיק {sub.n}</span>
                            <div style={{ height: 12, borderRadius: 3, background: 'var(--ex-bg-panel)', overflow: 'hidden' }}>
                              <div style={{ width: `${cohort ? (sub.reached / cohort) * 100 : 0}%`, height: '100%', background: 'var(--ex-green-bar, #0D9488)', opacity: 0.85, borderRadius: 3 }} />
                            </div>
                            <span style={{ fontSize: 12, textAlign: 'left', color: 'var(--ex-text-muted)' }}>
                              <b style={{ color: 'var(--ex-text-secondary)' }}>{fmt(sub.reached)}</b> · {spct}%
                              {sub.progressed > 0 && <span style={{ color: 'var(--ex-green-bar)', fontWeight: 700 }} title="עשו בדיוק כך מעקבי מנהל-תיק במחזור ואז דילגו למעקב הפסיכיאטר"> · דילגו {fmt(sub.progressed)}</span>}
                            </span>
                          </div>
                        );
                      })}
                      <div style={{ fontSize: 11.5, color: 'var(--ex-text-secondary)', paddingTop: 3, fontWeight: 600 }}>
                        יחס פגישות ~ 1 מעקב פסיכ׳ : {st.avg_cm} מעקבי מנהל-תיק
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {/* The money, as two columns rather than one figure. A single red number
              beside a stage reads as "this costs me"; what it actually was is a
              margin, and the fund's side of it was invisible (Ohad, 2026-10-07). */}
          {showMoney && (() => {
            const seq = seqData.sequence as { label: string; reached: number; revenue_per_patient: number;
              recognized_per_patient: number; cost_per_patient: number; value_per_patient: number; value_cumulative: number }[];
            const realised = seq.reduce((a, x) => a + x.value_per_patient * x.reached, 0);
            const full = seq.length ? seq[seq.length - 1].value_cumulative : 0;
            const td: React.CSSProperties = { padding: '5px 10px', textAlign: 'left', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
            return (
              <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--ex-border)' }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
                  מה נכנס ומה יוצא בכל שלב
                  <span style={{ fontWeight: 400, color: 'var(--ex-text-muted)' }}> · למטופל אחד · מכבי · לפני מע״מ</span>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', fontSize: 12.5, borderCollapse: 'collapse', minWidth: 520 }}>
                    <thead>
                      <tr style={{ color: 'var(--ex-text-muted)', fontSize: 11 }}>
                        <th style={{ textAlign: 'right', padding: '5px 10px', fontWeight: 700 }}>שלב</th>
                        <th style={{ ...td, fontWeight: 700 }}>הגיעו</th>
                        <th style={{ ...td, fontWeight: 700 }}>הקופה משלמת</th>
                        <th style={{ ...td, fontWeight: 700 }} title="שליש מהמנוי באינטייק מנהל התיק, שני שליש באינטייק הפסיכיאטר">הוכר לשלב</th>
                        <th style={{ ...td, fontWeight: 700 }}>אנחנו משלמים</th>
                        <th style={{ ...td, fontWeight: 700 }}>רווח לשלב</th>
                        <th style={{ ...td, fontWeight: 700 }}>שווי מטופל כאן</th>
                      </tr>
                    </thead>
                    <tbody>
                      {seq.map((x) => (
                        <tr key={x.label} style={{ borderTop: '1px solid var(--ex-border)' }}>
                          <td style={{ padding: '5px 10px' }}>{x.label}</td>
                          <td style={{ ...td, color: 'var(--ex-text-muted)' }}>{fmt(x.reached)}</td>
                          <td style={td}>{x.revenue_per_patient ? `₪${x.revenue_per_patient.toLocaleString('he-IL', { minimumFractionDigits: 2 })}` : '—'}</td>
                          <td style={td}>{x.recognized_per_patient ? `₪${x.recognized_per_patient.toLocaleString('he-IL', { minimumFractionDigits: 2 })}` : '—'}</td>
                          <td style={{ ...td, color: 'var(--ex-text-muted)' }}>₪{x.cost_per_patient.toLocaleString('he-IL', { minimumFractionDigits: 2 })}</td>
                          <td style={{ ...td, fontWeight: 700, color: x.value_per_patient < 0 ? '#EF4444' : 'inherit' }}>
                            ₪{x.value_per_patient.toLocaleString('he-IL', { minimumFractionDigits: 2 })}
                          </td>
                          <td style={{ ...td, fontWeight: 700 }}>₪{Math.round(x.value_cumulative).toLocaleString('he-IL')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--ex-text-secondary)', lineHeight: 1.75 }}>
                  מטופל שהשלים את כל המסע שווה <b>₪{Math.round(full).toLocaleString('he-IL')}</b> רווח ·
                  הקוהורטה הזו ייצרה <b>₪{Math.round(realised).toLocaleString('he-IL')}</b> בפועל
                  <div style={{ color: 'var(--ex-text-muted)', fontSize: 11.5 }}>
                    אינטייק הפסיכיאטר הוא השלב היחיד שמוריד את שווי המטופל — הוא עולה ואינו מניב,
                    כי הוא כבר שולם בתוך המנוי האבחוני.
                  </div>
                </div>
              </div>
            );
          })()}
        </div></div>
      )}

      {/* WHERE THEY ARE NOW — each patient once, at their current stage. Each bar is split
          by outcome: GREEN = will likely progress (a future meeting is booked); RED = won't
          progress (finished/closed OR genuinely stuck). Sums to the cohort. */}
      {showResults && (
      <div className="pat-unified"><div className="pat-unified-section">
        <div className="pat-panel-head">
          <span className="pat-panel-title">איפה נמצאים היום — פעילים בלבד</span>
          <span className="pat-panel-sub">רק מי שעדיין פעיל: יש לו תור עתידי (ירוק) או תקוע בלי תור (אדום). מי שסיים/נסגר לא מוצג כאן — ראו "סיבות נשירה". לחצו על "מעקב מנהל תיק" לפילוח 1/2/3/4.</span>
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: 'var(--ex-text-muted)', margin: '2px 0 10px' }}>
          {[['תקוע (בלי תור)', 'var(--ex-red-bar, #EF4444)'], ['תור עתידי', 'var(--ex-green-bar)']].map(([lab, clr]) => (
            <span key={lab} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: clr as string, display: 'inline-block' }} />{lab}
            </span>
          ))}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          {current.map((s) => {
            const active = curActive(s);
            if (active === 0) return null;   // active-only: skip stages with no active patient
            const segs = [
              { v: s.stuck, c: 'var(--ex-red-bar, #EF4444)' },           // active, no future meeting
              { v: s.scheduled, c: 'var(--ex-green-bar)' },              // has a future meeting
            ].filter((x) => x.v > 0);
            const expandable = Array.isArray(s.steps) && s.steps.length > 0;
            const isOpen = openCur.has(s.key);
            return (
              <div key={s.key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div title={`${fmt(s.finished)} סיימו/נסגרו · ${fmt(s.stuck)} תקוע · ${fmt(s.scheduled)} תור עתידי`}
                  style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {expandable ? (
                    <button onClick={() => toggleCur(s.key)}
                      style={{ width: 200, background: 'none', border: 'none', font: 'inherit', color: 'var(--ex-text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, padding: 0, textAlign: 'inherit' }}>
                      <span style={{ transform: isOpen ? 'rotate(90deg)' : 'none', display: 'inline-block', transition: 'transform .12s' }}>▸</span>{s.label}
                    </button>
                  ) : (
                    <span style={{ width: 200, fontSize: 13, color: 'var(--ex-text-secondary)' }}>{s.label}</span>
                  )}
                  <div style={{ flex: 1, background: 'var(--ex-bg-panel)', borderRadius: 6, height: 24, overflow: 'hidden' }}>
                    <div style={{ width: `${(active / curMax) * 100}%`, height: '100%', display: 'flex' }}>
                      {segs.map((seg, i) => (
                        <div key={i} style={{ flex: `${seg.v} 0 0`, background: seg.c, height: '100%' }} />
                      ))}
                    </div>
                  </div>
                  <span style={{ width: 170, textAlign: 'left', fontSize: 12.5, color: 'var(--ex-text-primary)' }}>
                    <b>{fmt(active)}</b> פעילים
                    {s.scheduled > 0 && <span style={{ color: 'var(--ex-green-bar)' }}> · {fmt(s.scheduled)} תור</span>}
                    {s.stuck > 0 && <button onClick={() => openStuck(s.key, s.label)} title="הצג את רשימת התקועים למטה" style={{ background: 'none', border: 'none', font: 'inherit', color: 'var(--ex-red-bar, #EF4444)', fontWeight: 700, cursor: 'pointer', padding: 0, textDecoration: stuckSel?.key === s.key ? 'underline' : 'underline dotted' }}> · {fmt(s.stuck)} תקוע ↓</button>}
                  </span>
                </div>
                {expandable && isOpen && (
                  <div style={{ paddingInlineStart: 24, display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 2 }}>
                    {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                    {s.steps.map((sub: any) => (
                      <div key={sub.n} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ width: 176, fontSize: 12, color: 'var(--ex-text-muted)' }}>{sub.n >= 4 ? '4+ מעקבי מנהל-תיק' : `${sub.n} מעקבי מנהל-תיק`}</span>
                        <div style={{ flex: 1, background: 'var(--ex-bg-panel)', borderRadius: 5, height: 12, overflow: 'hidden' }}>
                          <div style={{ width: `${Math.min(100, sub.pct)}%`, height: '100%', background: 'var(--ex-green-bar, #0D9488)', opacity: 0.7, borderRadius: 5 }} />
                        </div>
                        <span style={{ width: 110, textAlign: 'left', fontSize: 12, color: 'var(--ex-text-muted)' }}>{fmt(sub.reached)} · {sub.pct}%</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {(() => {
            const totFin = current.reduce((a: number, s: { finished?: number }) => a + (s.finished || 0), 0);
            if (!totFin) return null;
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6, paddingTop: 9, borderTop: '1px solid var(--ex-border)' }}>
                <span style={{ width: 200, fontSize: 13, color: 'var(--ex-text-muted)' }}>סיימו / נסגרו (כל השלבים)</span>
                <div style={{ flex: 1, background: 'var(--ex-bg-panel)', borderRadius: 6, height: 24, overflow: 'hidden' }}>
                  <div style={{ width: '100%', height: '100%', background: 'var(--ex-text-muted)', opacity: 0.45 }} />
                </div>
                <span style={{ width: 170, textAlign: 'left', fontSize: 12.5, color: 'var(--ex-text-muted)' }}>
                  <b>{fmt(totFin)}</b> · {cohort ? Math.round((totFin / cohort) * 100) : 0}%
                </span>
              </div>
            );
          })()}
        </div>
      </div></div>
      )}

      {stuckSel && (
        <div className="pat-unified"><div className="pat-unified-section">
          <div className="pat-panel-head">
            <span className="pat-panel-title">תקועים · {stuckSel.label}{stuckList ? ` (${stuckList.length})` : ''}</span>
            <button onClick={() => { setStuckSel(null); setStuckList(null); }} style={{ background: 'none', border: 'none', color: 'var(--ex-text-muted)', cursor: 'pointer', fontSize: 13 }}>סגור ✕</button>
          </div>
          {stuckLoading ? (
            <div style={{ color: 'var(--ex-text-muted)', fontSize: 13, padding: 4 }}>טוען…</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ color: 'var(--ex-text-muted)', textAlign: 'right', fontSize: 12 }}>
                    <th style={{ padding: '6px 10px', fontWeight: 600 }}>מזהה</th>
                    <th style={{ padding: '6px 10px', fontWeight: 600 }}>קופה</th>
                    <th style={{ padding: '6px 10px', fontWeight: 600 }}>ימי המתנה</th>
                  </tr>
                </thead>
                <tbody>
                  {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                  {(stuckList ?? []).map((p: any) => (
                    <tr key={p.internal_id} style={{ borderTop: '1px solid var(--ex-border)' }}>
                      <td style={{ padding: '6px 10px' }}>
                        <PatientCardLink linked={canOpenPatient} href={`${CLINICUI}?id=${p.internal_id}`}
                          style={{ color: canOpenPatient ? 'var(--ex-blue-text)' : 'var(--ex-text-secondary)', fontWeight: 600 }}
                          title="פתח כרטיס מטופל במערכת">{p.serial || p.internal_id}</PatientCardLink>
                      </td>
                      <td style={{ padding: '6px 10px', color: 'var(--ex-text-muted)' }}>{p.clinic}</td>
                      <td style={{ padding: '6px 10px', color: p.waiting_days > 45 ? 'var(--ex-red-bar, #EF4444)' : 'var(--ex-text-secondary)', fontWeight: p.waiting_days > 45 ? 700 : 400 }}>{p.waiting_days}</td>
                    </tr>
                  ))}
                  {(stuckList ?? []).length === 0 && (
                    <tr><td colSpan={3} style={{ padding: 10, color: 'var(--ex-text-muted)' }}>אין תקועים בשלב זה.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div></div>
      )}

      {showResults && closureTotal > 0 && (
        <div className="pat-unified"><div className="pat-unified-section">
          <div className="pat-panel-head">
            <span className="pat-panel-title">הסיבה של כל מטופל בקוהורט ({fmt(closureTotal)})</span>
            <span className="pat-panel-sub">כל הקוהורט, לפי סיבה (סכום = {fmt(cohort)}) · אפור = סיים/נסגר · אדום = תקוע (בלי תור) · ירוק = תור עתידי · "נסגר על אי-תגובה" = auto-close של מדיניות-ההזמנות, לא החלטה קלינית.</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {closure.map((c) => (
              <div key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ width: 250, fontSize: 13, color: 'var(--ex-text-secondary)' }}>{c.label}</span>
                <div style={{ flex: 1, background: 'var(--ex-bg-panel)', borderRadius: 6, height: 22, overflow: 'hidden' }}>
                  <div style={{ width: `${closureTotal ? (c.count / closureTotal) * 100 : 0}%`, height: '100%', background: CAT_COLOR[c.cat] ?? 'var(--ex-text-muted)' }} />
                </div>
                <span style={{ width: 110, textAlign: 'left', fontSize: 13, fontWeight: 700, color: 'var(--ex-text-primary)' }}>{fmt(c.count)} · {closureTotal ? Math.round((c.count / closureTotal) * 100) : 0}%</span>
              </div>
            ))}
          </div>
        </div></div>
      )}
    </div>
  );
}
